import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  connectMetadataElementSteps,
  connectSfStages,
  connectThirdPartyFunnelSteps,
} from '../../../../../reducers/united-canvas/unitedCanvasReducer';
import { ConnectStepProps } from './ConnectSteps';
import { FunnelType } from '../../../../../types/enums/FunnelType';
import { OnCollapsedListItemClick } from '../../../../common/CollapseList';
import { getFunnelTypeFromNodeId } from '../../../../funnel-map-canvas/helper';
import { OnConnectFunnelOrStepsClick } from '../../../../funnel-map-canvas/types';
import { getGroupsForSalesforceFunnel, getGroupsForSalesforceStep } from './getGroupsForSalesforce';
import { ConnectStepsListGroup, CurrentStep } from './types';
import { getGroupsForThirdPartyFunnel, getGroupsForThirdPartyStep } from './getGroupsForThirdParty';
import { getGroupsForMetadataElement } from './getGroupsForMetadataElement';
import { useGetObjectTypesLabel } from '../../../../../hooks/object-types/useGetObjectTypesLabel';

export const useConnectSteps = (funnelMap: FunnelMap) => {
  const { getObjectTypeLabel } = useGetObjectTypesLabel();
  const { funnelsData, thirdPartyFunnelsData, metadataElementsData, metadataElements } = funnelMap;

  const dispatch = useDispatch();
  const [connectStepsAnchorEl, setConnectStepsAnchorEl] = useState<HTMLElement | null>(null);
  const [holdNodeHighlighted, setHoldNodeHighlighted] = useState(false);

  const [currentStageAndFunnel, setCurrentStageAndFunnel] = useState<CurrentStep>();

  const handleConnectStepsButtonClick: OnConnectFunnelOrStepsClick = useCallback(
    (event, { funnelId, stepId: stageId, funnelType }, parentEl) => {
      if (funnelType === FunnelType.THIRD_PARTY) {
        setCurrentStageAndFunnel({ funnelId, funnelType, stageId });
        setConnectStepsAnchorEl(parentEl);
        setHoldNodeHighlighted(true);
      }

      if (funnelType === FunnelType.SALESFORCE) {
        setCurrentStageAndFunnel({ funnelId, stageId, funnelType });
        setConnectStepsAnchorEl(parentEl);
        setHoldNodeHighlighted(true);
      }
      if (funnelType === FunnelType.METADATA_ELEMENT) {
        setCurrentStageAndFunnel({ funnelId, stageId, funnelType: FunnelType.METADATA_ELEMENT });
        setConnectStepsAnchorEl(parentEl);
        setHoldNodeHighlighted(true);
      }
    },
    [],
  );

  const handleConnectStepsButtonClose = () => {
    setConnectStepsAnchorEl(null);
    setHoldNodeHighlighted(false);
  };

  const addConnection = useCallback(
    (targetFunnelId: string, targetStageId?: string) => {
      if (!currentStageAndFunnel) {
        console.error('Unexpected. No currentStageAndFunnel');
        return;
      }
      const {
        funnelId: sourceFunnelId,
        funnelType: sourceFunnelType,
        stageId: sourceStageId,
      } = currentStageAndFunnel;

      const targetFunnelType = getFunnelTypeFromNodeId(funnelMap, targetFunnelId);
      if (!targetFunnelType) {
        console.error('Unexpected. No targetFunnelType found');
        return;
      }

      if (sourceFunnelType === FunnelType.SALESFORCE) {
        dispatch(
          connectSfStages({
            stageA: {
              funnelId: sourceFunnelId,
              stageId: sourceStageId,
            },
            stageB: {
              funnelId: targetFunnelId,
              stageId: targetStageId,
              funnelType: targetFunnelType,
            },
          }),
        );
      }

      if (sourceFunnelType === FunnelType.THIRD_PARTY) {
        dispatch(
          connectThirdPartyFunnelSteps({
            stageA: {
              funnelId: sourceFunnelId,
              stageId: sourceStageId,
            },
            stageB: {
              funnelId: targetFunnelId,
              stageId: targetStageId,
              funnelType: targetFunnelType,
            },
          }),
        );
      }

      if (sourceFunnelType === FunnelType.METADATA_ELEMENT) {
        dispatch(
          connectMetadataElementSteps({
            originFullMetadataId: sourceFunnelId,
            target: {
              funnelId: targetFunnelId,
              stageId: targetStageId,
              funnelType: targetFunnelType,
            },
          }),
        );
      }

      handleConnectStepsButtonClose();
    },
    [currentStageAndFunnel, dispatch, funnelMap],
  );

  const stepGroups: ConnectStepsListGroup[] = useMemo(() => {
    if (!currentStageAndFunnel) {
      return [];
    }
    if (currentStageAndFunnel.funnelType === FunnelType.SALESFORCE) {
      if (currentStageAndFunnel.stageId) {
        return getGroupsForSalesforceStep({
          step: currentStageAndFunnel,
          funnelsData,
          thirdPartyFunnelsData,
          metadataElementsData,
          getObjectTypeLabel,
        });
      } else {
        return getGroupsForSalesforceFunnel({
          step: currentStageAndFunnel,
          funnelsData,
          thirdPartyFunnelsData,
          metadataElementsData,
          getObjectTypeLabel,
        });
      }
    }
    if (currentStageAndFunnel.funnelType === FunnelType.THIRD_PARTY) {
      if (currentStageAndFunnel.stageId) {
        return getGroupsForThirdPartyStep({
          step: currentStageAndFunnel,
          funnelsData,
          thirdPartyFunnelsData,
          metadataElementsData,
          getObjectTypeLabel,
        });
      } else {
        return getGroupsForThirdPartyFunnel({
          step: currentStageAndFunnel,
          funnelsData,
          thirdPartyFunnelsData,
          metadataElementsData,
          getObjectTypeLabel,
        });
      }
    }
    if (currentStageAndFunnel.funnelType === FunnelType.METADATA_ELEMENT) {
      return getGroupsForMetadataElement({
        step: currentStageAndFunnel,
        funnelsData,
        thirdPartyFunnelsData,
        metadataElementsData,
        metadataElements,
        getObjectTypeLabel,
      });
    }
    return [];
  }, [
    currentStageAndFunnel,
    funnelsData,
    metadataElements,
    metadataElementsData,
    thirdPartyFunnelsData,
    getObjectTypeLabel,
  ]);

  const onItemClick: OnCollapsedListItemClick = useCallback(
    ({ item, parentGroup }) => {
      const targetStageId = item?.value;
      const targetFunnelId = parentGroup.value;

      addConnection(targetFunnelId, targetStageId);
    },
    [addConnection],
  );

  const connectStepsProps: ConnectStepProps = useMemo(
    () => ({
      anchorEl: connectStepsAnchorEl || null,
      handleClose: handleConnectStepsButtonClose,
      stepGroups,
      onItemClick,
      startAllExpanded: false,
    }),
    [connectStepsAnchorEl, onItemClick, stepGroups],
  );

  return {
    handleConnectStepsButtonClick,
    connectStepsProps,
    holdNodeHighlighted,
  };
};
